import React, { useState, useEffect, useCallback } from 'react';
import { Search, Eye, FileText, Download, Mail, Calendar } from 'lucide-react';
import { useApp } from '../../../context/AppContext';
import apiService from '../../../services/apiService';
import SucursalFilter from '../../../components/filters/SucursalFilter';
import { formatMoney, formatDate } from '../../../utils/formatters';
import FacturaDetailModal from './FacturaDetailModal';

const HistorialFacturasTab = ({ catalogos }) => {
  const { showToast, loading, setLoading } = useApp();

  // Estado de filtros
  const [sucursalId, setSucursalId] = useState('');
  const [filtros, setFiltros] = useState({
    fecha_inicio: '',
    fecha_fin: '',
    cliente_id: '',
    estado: '',
    folio: ''
  });

  // Estado de facturas
  const [facturas, setFacturas] = useState([]);
  const [facturaSeleccionada, setFacturaSeleccionada] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  // Clientes para el filtro
  const [clientes, setClientes] = useState([]);

  // Cargar clientes y facturas iniciales
  useEffect(() => {
    cargarClientes();
    cargarFacturas();
  }, []);

  const cargarClientes = async () => {
    try {
      const result = await apiService.getClientes();
      if (result.success) {
        setClientes(result.data);
      }
    } catch (error) {
      console.error('Error cargando clientes:', error);
    }
  };

  const cargarFacturas = async () => {
    try {
      setLoading(true);
      const result = await apiService.getFacturas({ ...filtros, sucursal: sucursalId });
      if (result.success) {
        setFacturas(result.data);
      } else {
        showToast(result.message || 'Error al cargar facturas', 'error');
      }
    } catch (error) {
      console.error('Error cargando facturas:', error);
      showToast('Error al cargar facturas', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleFiltroChange = (field, value) => {
    setFiltros(prev => ({ ...prev, [field]: value }));
  };

  const handleBuscar = () => {
    cargarFacturas();
  };

  const handleVerDetalle = async (factura) => {
    try {
      setLoading(true);
      const result = await apiService.getFactura(factura.id);
      if (result.success) {
        setFacturaSeleccionada(result.data);
        setShowDetailModal(true);
      } else {
        showToast('Error al cargar detalle de factura', 'error');
      }
    } catch (error) {
      console.error('Error cargando detalle:', error);
      showToast('Error al cargar detalle', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDescargarXML = async (factura) => {
    try {
      const result = await apiService.descargarFacturaXML(factura.id);
      if (result.success && result.data.url) {
        window.open(result.data.url, '_blank');
      } else {
        showToast(result.message || 'XML no disponible', 'warning');
      }
    } catch (error) {
      showToast('Error al descargar XML', 'error');
    }
  };

  const handleDescargarPDF = async (factura) => {
    try {
      const result = await apiService.descargarFacturaPDF(factura.id);
      if (result.success && result.data.url) {
        window.open(result.data.url, '_blank');
      } else {
        showToast(result.message || 'PDF no disponible', 'warning');
      }
    } catch (error) {
      showToast('Error al descargar PDF', 'error');
    }
  };

  const handleReenviar = async (factura) => {
    if (!factura.receptor_email) {
      showToast('El cliente no tiene email registrado', 'warning');
      return;
    }

    try {
      setLoading(true);
      const result = await apiService.reenviarFactura(factura.id);
      if (result.success) {
        showToast(`Factura enviada a ${result.data.email}`, 'success');
      } else {
        showToast(result.message || 'Error al reenviar', 'error');
      }
    } catch (error) {
      showToast('Error al reenviar factura', 'error');
    } finally {
      setLoading(false);
    }
  };

  const getEstadoClass = (estado) => {
    switch (estado) {
      case 'vigente': return 'vigente';
      case 'cancelada': return 'cancelada';
      case 'pendiente_timbrado': return 'pendiente_timbrado';
      default: return '';
    }
  };

  const getEstadoLabel = (estado) => {
    switch (estado) {
      case 'vigente': return 'Vigente';
      case 'cancelada': return 'Cancelada';
      case 'pendiente_timbrado': return 'Pendiente';
      default: return estado;
    }
  };

  return (
    <div className="historial-container">
      {/* Filtros */}
      <div className="historial-filters">
        <div className="filter-group">
          <label>Fecha Inicio</label>
          <input
            type="date"
            value={filtros.fecha_inicio}
            onChange={(e) => handleFiltroChange('fecha_inicio', e.target.value)}
          />
        </div>

        <div className="filter-group">
          <label>Fecha Fin</label>
          <input
            type="date"
            value={filtros.fecha_fin}
            onChange={(e) => handleFiltroChange('fecha_fin', e.target.value)}
          />
        </div>

        <div className="filter-group">
          <label>Cliente</label>
          <select
            value={filtros.cliente_id}
            onChange={(e) => handleFiltroChange('cliente_id', e.target.value)}
          >
            <option value="">Todos</option>
            {clientes.map(c => (
              <option key={c.id} value={c.id}>{c.nombre}</option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label>Estado</label>
          <select
            value={filtros.estado}
            onChange={(e) => handleFiltroChange('estado', e.target.value)}
          >
            <option value="">Todos</option>
            <option value="vigente">Vigente</option>
            <option value="pendiente_timbrado">Pendiente</option>
            <option value="cancelada">Cancelada</option>
          </select>
        </div>

        <div className="filter-group">
          <label>Folio</label>
          <input
            type="text"
            placeholder="Buscar folio..."
            value={filtros.folio}
            onChange={(e) => handleFiltroChange('folio', e.target.value)}
          />
        </div>

        <SucursalFilter value={sucursalId} onChange={setSucursalId} />

        <button className="btn-buscar" onClick={handleBuscar}>
          <Search size={16} />
          Buscar
        </button>
      </div>

      {/* Tabla de facturas */}
      <div className="facturas-table-container">
        {loading ? (
          <div className="loading-overlay">
            <div className="loading-spinner"></div>
            <p>Cargando facturas...</p>
          </div>
        ) : facturas.length === 0 ? (
          <div className="empty-state">
            <FileText size={48} />
            <h4>No hay facturas</h4>
            <p>No se encontraron facturas con los filtros seleccionados</p>
          </div>
        ) : (
          <table className="facturas-table">
            <thead>
              <tr>
                <th>Folio</th>
                <th>Fecha</th>
                <th>Cliente</th>
                <th>RFC</th>
                <th>Total</th>
                <th>Estado</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {facturas.map(factura => (
                <tr key={factura.id}>
                  <td>
                    <strong style={{ color: '#667eea' }}>{factura.folio}</strong>
                  </td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <Calendar size={14} color="#9ca3af" />
                      {formatDate(factura.fecha_emision)}
                    </div>
                  </td>
                  <td>{factura.cliente_nombre || factura.receptor_razon_social}</td>
                  <td>{factura.receptor_rfc}</td>
                  <td style={{ fontWeight: '600', color: '#059669' }}>
                    {formatMoney(factura.total)}
                  </td>
                  <td>
                    <span className={`estado-badge ${getEstadoClass(factura.estado)}`}>
                      {getEstadoLabel(factura.estado)}
                    </span>
                  </td>
                  <td>
                    <div className="acciones-cell">
                      <button
                        className="btn-accion primary"
                        onClick={() => handleVerDetalle(factura)}
                        title="Ver detalle"
                      >
                        <Eye size={14} />
                      </button>
                      <button
                        className="btn-accion"
                        onClick={() => handleDescargarPDF(factura)}
                        title="Descargar PDF"
                        disabled={factura.estado === 'pendiente_timbrado'}
                      >
                        <Download size={14} />
                      </button>
                      <button
                        className="btn-accion"
                        onClick={() => handleReenviar(factura)}
                        title="Reenviar por email"
                        disabled={factura.estado === 'pendiente_timbrado'}
                      >
                        <Mail size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Modal de detalle */}
      {showDetailModal && facturaSeleccionada && (
        <FacturaDetailModal
          factura={facturaSeleccionada}
          catalogos={catalogos}
          onClose={() => {
            setShowDetailModal(false);
            setFacturaSeleccionada(null);
          }}
          onDescargarXML={() => handleDescargarXML(facturaSeleccionada)}
          onDescargarPDF={() => handleDescargarPDF(facturaSeleccionada)}
          onReenviar={() => handleReenviar(facturaSeleccionada)}
        />
      )}
    </div>
  );
};

export default HistorialFacturasTab;
